import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container, Box, Grid, TextField, Button, Typography, Link,
  Paper, Avatar, InputAdornment, IconButton, Alert, Snackbar,
  Stepper, Step, StepLabel, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormControlLabel, Chip, OutlinedInput, FormHelperText,
  ListItemText, Divider
} from '@mui/material';
import {
  PersonAddOutlined as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  ExploreOutlined as ExploreIcon,
  LanguageOutlined as LanguageIcon,
  InterestsOutlined as InterestsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import magellanLogo from '../components/Magellan Languages-Transparent-01.png'
import { getLanguageOptions, PROFICIENCY_LEVELS } from '../config/languagesConfig';

// Language + proficiency options come from the central config (includes es-419, pt-BR).
const languageOptions = getLanguageOptions();
const proficiencyLevels = PROFICIENCY_LEVELS;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  // Step state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Account Information', 'Language Preferences', 'Interests'];

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    registration_code: '',
    first_name: '',
    last_name: '',
    native_language: 'en',
    learning_language: 'fr',
    proficiency_level: 'A1',
    interests: []
  });

  // Available interests from the API
  const [availableInterests, setAvailableInterests] = useState([]);
  const [interestsByCategory, setInterestsByCategory] = useState({});

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeParam = urlParams.get('code');

    if (codeParam) {
      setFormData(prev => ({
        ...prev,
        registration_code: codeParam
      }));
    }
  }, []);

  // Fetch interests from the API
  useEffect(() => {
    const fetchInterests = async () => {
      try {
        const { data } = await apiClient.auth.getInterests();
        setAvailableInterests(data.interests || []);

        // Group interests by category
        const grouped = {};
        data.interests.forEach(interest => {
          if (!grouped[interest.category]) {
            grouped[interest.category] = [];
          }
          grouped[interest.category].push(interest);
        });
        setInterestsByCategory(grouped);
      } catch (error) {
        console.error('Error fetching interests:', error);
      }
    };

    fetchInterests();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle interest selection
  const handleInterestChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      interests: value
    }));
  };

  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Validate the current step
  const validateStep = () => {
    const errors = {};

    if (activeStep === 0) {
      // Validate account information
      if (!formData.email) errors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';

      if (!formData.password) errors.password = 'Password is required';
      else if (formData.password.length < 6) errors.password = 'Password must be at least 6 characters';

      if (!formData.confirmPassword) errors.confirmPassword = 'Please confirm your password';
      else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }

      if (!formData.first_name) errors.first_name = 'First name is required';
      if (!formData.registration_code) errors.registration_code = 'Registration Code is required';
      if (!formData.last_name) errors.last_name = 'Last name is required';
    } else if (activeStep === 1) {
      // Validate language preferences
      if (!formData.native_language) errors.native_language = 'Native language is required';
      if (!formData.learning_language) errors.learning_language = 'Learning language is required';
      if (formData.learning_language === formData.native_language) {
        errors.learning_language = 'Learning language must be different from native language';
      }
    }
    // No validation for interests in step 2 (they're optional)

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep()) {
      setActiveStep(prevStep => prevStep + 1);
    }
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Format the data for the API
      const apiData = {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        registration_code: formData.registration_code,
        native_language: formData.native_language,
        languages: [
          {
            language_code: formData.learning_language,
            proficiency_level: formData.proficiency_level
          }
        ],
        interests: formData.interests
      };

      const result = await register(apiData);

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
        setSnackbarOpen(true);
        // If it's an email error, go back to the first step
        if (result.error.includes('email')) {
          setActiveStep(0);
          setFormErrors({ email: result.error });
        }
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Render the current step content
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="first_name"
                  label="First Name"
                  name="first_name"
                  autoComplete="given-name"
                  value={formData.first_name}
                  onChange={handleChange}
                  error={!!formErrors.first_name}
                  helperText={formErrors.first_name}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="last_name"
                  label="Last Name"
                  name="last_name"
                  autoComplete="family-name"
                  value={formData.last_name}
                  onChange={handleChange}
                  error={!!formErrors.last_name}
                  helperText={formErrors.last_name}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleTogglePassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="registration_code"
                  label="Registration Code"
                  name="registration_code"
                  autoComplete="registration-code"
                  value={formData.registration_code}
                  onChange={handleChange}
                  error={!!formErrors.registration_code}
                  helperText={formErrors.registration_code}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Language Preferences
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select
                  required
                  fullWidth
                  id="native_language"
                  label="Native Language"
                  name="native_language"
                  value={formData.native_language}
                  onChange={handleChange}
                  error={!!formErrors.native_language}
                  helperText={formErrors.native_language || ' '}
                  disabled={loading}
                >
                  {languageOptions.map(language => (
                    <MenuItem key={language.code} value={language.code}>
                      {language.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  select
                  required
                  fullWidth
                  id="learning_language"
                  label="Language I Want to Learn"
                  name="learning_language"
                  value={formData.learning_language}
                  onChange={handleChange}
                  error={!!formErrors.learning_language}
                  helperText={formErrors.learning_language || ' '}
                  disabled={loading}
                >
                  {languageOptions
                    .filter(lang => lang.code !== formData.native_language)
                    .map(language => (
                      <MenuItem key={language.code} value={language.code}>
                        {language.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  select
                  required
                  fullWidth
                  id="proficiency_level"
                  label="My Proficiency Level"
                  name="proficiency_level"
                  value={formData.proficiency_level}
                  onChange={handleChange}
                  helperText=" "
                  disabled={loading}
                >
                  {proficiencyLevels.map(level => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Interests
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Select topics you're interested in learning about. This helps us tailor content to your preferences.
            </Typography>

            {Object.entries(interestsByCategory).map(([category, interests]) => (
              <Box key={category} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  {category}
                </Typography>
                <Grid container spacing={1}>
                  {interests.map(interest => (
                    <Grid item key={interest.id}>
                      <Chip
                        label={interest.name}
                        onClick={() => {
                          const currentInterests = [...formData.interests];
                          const index = currentInterests.indexOf(interest.id);

                          if (index === -1) {
                            // Add interest
                            setFormData({
                              ...formData,
                              interests: [...currentInterests, interest.id]
                            });
                          } else {
                            // Remove interest
                            currentInterests.splice(index, 1);
                            setFormData({
                              ...formData,
                              interests: currentInterests
                            });
                          }
                        }}
                        color={formData.interests.includes(interest.id) ? "primary" : "default"}
                        variant={formData.interests.includes(interest.id) ? "filled" : "outlined"}
                        clickable
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          mt: 4,
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4
          }}>
            {/* App Logo */}
            <Box
              component="img"
              src={magellanLogo}
              sx={{
                height: 200,
                mr: 0
              }}
              alt="Magellan Logo"
            />


          </Box>

          <Typography component="h1" variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
            Create Your Account
          </Typography>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box component="div" sx={{ mt: 3 }}>
            {getStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                startIcon={<BackIcon />}
              >
                Back
              </Button>
              <Box>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NextIcon />}
                    disabled={loading}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Already have an account? Sign in
            </Link>
          </Box>
        </Paper>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RegisterPage;